import React, { createContext, useContext, useState, useMemo, useCallback, useEffect, startTransition, ReactNode } from 'react';

export type Theme =
  | 'light'
  | 'dark'
  | 'blue'
  | 'green'
  | 'purple'
  | 'macos';

export interface ThemeColors {
  // Background colors
  background: string;
  surface: string;
  surfaceHover: string;
  /** Header / bar background (e.g. detail view header) */
  headerBg: string;
  /** Hover class for secondary actions (e.g. Back button) - include variant: 'hover:bg-gray-100' */
  interactiveHover: string;
  
  // Text colors
  text: string;
  textSecondary: string;
  textMuted: string;
  
  // Border colors
  border: string;
  borderLight: string;
  /** Divider within content (lighter than border) */
  divide: string;
  
  // Sidebar colors
  sidebarBackground: string;
  sidebarText: string;
  sidebarTextHover: string;
  /** Sidebar item hover (full classes e.g. 'hover:bg-white/10 hover:text-white') */
  sidebarItemHover: string;
  sidebarActive: string;
  sidebarActiveText: string;
  sidebarBorder: string;
  
  // Topbar colors
  topbarBackground: string;
  topbarText: string;
  topbarBorder: string;
  
  // Primary colors
  primary: string;
  primaryHover: string;
  primaryText: string;
  /** Text/icon color for primary accent (e.g. link, icon when selected) */
  primaryFg: string;
  /** Border color for primary accent (e.g. card left border) */
  primaryBorder: string;
  /** Primary border on all sides (e.g. outline toggle when pressed) */
  primaryBorderAll: string;
  /** Switch/toggle checked track - full class e.g. 'peer-checked:bg-teal-600' */
  primaryChecked: string;
  /** Switch checked hover - full class e.g. 'peer-checked:hover:bg-teal-700' */
  primaryCheckedHover: string;
  
  // Accent colors
  accent: string;
  accentHover: string;
  
  // Status colors
  success: string;
  warning: string;
  error: string;
  info: string;
}

const themes: Record<Theme, ThemeColors> = {
  light: {
    background: 'bg-slate-50',
    surface: 'bg-white',
    surfaceHover: 'bg-slate-100',
    headerBg: 'bg-slate-50/80',
    interactiveHover: 'hover:bg-slate-100',
    text: 'text-slate-900',
    textSecondary: 'text-slate-600',
    textMuted: 'text-slate-500',
    border: 'border-slate-200/80',
    borderLight: 'border-slate-100',
    divide: 'divide-slate-100',
    sidebarBackground: 'bg-gradient-to-b from-slate-950 via-slate-900 to-slate-900',
    sidebarText: 'text-slate-300',
    sidebarTextHover: 'text-white',
    sidebarItemHover: 'hover:bg-white/10 hover:text-white',
    sidebarActive: 'bg-teal-600/90 shadow-sm shadow-teal-900/40',
    sidebarActiveText: 'text-white',
    sidebarBorder: 'border-slate-800/80',
    topbarBackground: 'bg-white/90 backdrop-blur-md',
    topbarText: 'text-slate-900',
    topbarBorder: 'border-slate-200/80',
    primary: 'bg-teal-600',
    primaryHover: 'bg-teal-700',
    primaryText: 'text-white',
    primaryFg: 'text-teal-700',
    primaryBorder: 'border-l-teal-500',
    primaryBorderAll: 'border-teal-500',
    primaryChecked: 'peer-checked:bg-teal-600',
    primaryCheckedHover: 'peer-checked:hover:bg-teal-700',
    accent: 'bg-sky-600',
    accentHover: 'bg-sky-700',
    success: 'bg-emerald-600 text-white',
    warning: 'bg-amber-500 text-white',
    error: 'bg-rose-600 text-white',
    info: 'bg-sky-600 text-white',
  },
  dark: {
    background: 'bg-slate-950',
    surface: 'bg-slate-900',
    surfaceHover: 'bg-slate-800',
    headerBg: 'bg-slate-900',
    interactiveHover: 'hover:bg-slate-800',
    text: 'text-slate-100',
    textSecondary: 'text-slate-300',
    textMuted: 'text-slate-500',
    border: 'border-slate-800',
    borderLight: 'border-slate-900',
    divide: 'divide-slate-800',
    sidebarBackground: 'bg-gradient-to-b from-black to-slate-950',
    sidebarText: 'text-slate-300',
    sidebarTextHover: 'text-white',
    sidebarItemHover: 'hover:bg-white/10 hover:text-white',
    sidebarActive: 'bg-teal-500/90 shadow-sm shadow-teal-950/50',
    sidebarActiveText: 'text-white',
    sidebarBorder: 'border-slate-800',
    topbarBackground: 'bg-slate-900/90 backdrop-blur-md',
    topbarText: 'text-slate-100',
    topbarBorder: 'border-slate-800',
    primary: 'bg-teal-500',
    primaryHover: 'bg-teal-400',
    primaryText: 'text-slate-950',
    primaryFg: 'text-teal-400',
    primaryBorder: 'border-l-teal-400',
    primaryBorderAll: 'border-teal-400',
    primaryChecked: 'peer-checked:bg-teal-500',
    primaryCheckedHover: 'peer-checked:hover:bg-teal-400',
    accent: 'bg-sky-500',
    accentHover: 'bg-sky-400',
    success: 'bg-emerald-600 text-white',
    warning: 'bg-amber-500 text-white',
    error: 'bg-rose-600 text-white',
    info: 'bg-sky-600 text-white',
  },
  blue: {
    background: 'bg-blue-50',
    surface: 'bg-white',
    surfaceHover: 'bg-blue-100',
    headerBg: 'bg-blue-50',
    interactiveHover: 'hover:bg-blue-100',
    text: 'text-gray-900',
    textSecondary: 'text-gray-700',
    textMuted: 'text-gray-500',
    border: 'border-blue-200',
    borderLight: 'border-blue-100',
    divide: 'divide-blue-100',
    sidebarBackground: 'bg-gradient-to-b from-blue-900 to-blue-800',
    sidebarText: 'text-blue-100',
    sidebarTextHover: 'text-white',
    sidebarItemHover: 'hover:bg-blue-700/50 hover:text-white',
    sidebarActive: 'bg-gradient-to-r from-blue-600 to-blue-500',
    sidebarActiveText: 'text-white',
    sidebarBorder: 'border-blue-700',
    topbarBackground: 'bg-white',
    topbarText: 'text-gray-900',
    topbarBorder: 'border-blue-200',
    primary: 'bg-blue-600',
    primaryHover: 'bg-blue-700',
    primaryText: 'text-white',
    primaryFg: 'text-blue-600',
    primaryBorder: 'border-l-blue-500',
    primaryBorderAll: 'border-blue-500',
    primaryChecked: 'peer-checked:bg-blue-600',
    primaryCheckedHover: 'peer-checked:hover:bg-blue-700',
    accent: 'bg-cyan-600',
    accentHover: 'bg-cyan-700',
    success: 'bg-emerald-600 text-white',
    warning: 'bg-amber-500 text-white',
    error: 'bg-rose-600 text-white',
    info: 'bg-sky-600 text-white',
  },
  green: {
    background: 'bg-green-50',
    surface: 'bg-white',
    surfaceHover: 'bg-green-100',
    headerBg: 'bg-green-50',
    interactiveHover: 'hover:bg-green-100',
    text: 'text-gray-900',
    textSecondary: 'text-gray-700',
    textMuted: 'text-gray-500',
    border: 'border-green-200',
    borderLight: 'border-green-100',
    divide: 'divide-green-100',
    sidebarBackground: 'bg-gradient-to-b from-green-900 to-green-800',
    sidebarText: 'text-green-100',
    sidebarTextHover: 'text-white',
    sidebarItemHover: 'hover:bg-green-700/50 hover:text-white',
    sidebarActive: 'bg-gradient-to-r from-green-600 to-emerald-500',
    sidebarActiveText: 'text-white',
    sidebarBorder: 'border-green-700',
    topbarBackground: 'bg-white',
    topbarText: 'text-gray-900',
    topbarBorder: 'border-green-200',
    primary: 'bg-green-600',
    primaryHover: 'bg-green-700',
    primaryText: 'text-white',
    primaryFg: 'text-green-600',
    primaryBorder: 'border-l-green-500',
    primaryBorderAll: 'border-green-500',
    primaryChecked: 'peer-checked:bg-green-600',
    primaryCheckedHover: 'peer-checked:hover:bg-green-700',
    accent: 'bg-emerald-600',
    accentHover: 'bg-emerald-700',
    success: 'bg-emerald-600 text-white',
    warning: 'bg-amber-500 text-white',
    error: 'bg-rose-600 text-white',
    info: 'bg-sky-600 text-white',
  },
  purple: {
    background: 'bg-purple-50',
    surface: 'bg-white',
    surfaceHover: 'bg-purple-100',
    headerBg: 'bg-purple-50',
    interactiveHover: 'hover:bg-purple-100',
    text: 'text-gray-900',
    textSecondary: 'text-gray-700',
    textMuted: 'text-gray-500',
    border: 'border-purple-200',
    borderLight: 'border-purple-100',
    divide: 'divide-purple-100',
    sidebarBackground: 'bg-gradient-to-b from-purple-900 to-purple-800',
    sidebarText: 'text-purple-100',
    sidebarTextHover: 'text-white',
    sidebarItemHover: 'hover:bg-purple-700/50 hover:text-white',
    sidebarActive: 'bg-gradient-to-r from-purple-600 to-pink-500',
    sidebarActiveText: 'text-white',
    sidebarBorder: 'border-purple-700',
    topbarBackground: 'bg-white',
    topbarText: 'text-gray-900',
    topbarBorder: 'border-purple-200',
    primary: 'bg-purple-600',
    primaryHover: 'bg-purple-700',
    primaryText: 'text-white',
    primaryFg: 'text-purple-600',
    primaryBorder: 'border-l-purple-500',
    primaryBorderAll: 'border-purple-500',
    primaryChecked: 'peer-checked:bg-purple-600',
    primaryCheckedHover: 'peer-checked:hover:bg-purple-700',
    accent: 'bg-pink-600',
    accentHover: 'bg-pink-700',
    success: 'bg-emerald-600 text-white',
    warning: 'bg-amber-500 text-white',
    error: 'bg-rose-600 text-white',
    info: 'bg-sky-600 text-white',
  },
  macos: {
    background: 'bg-slate-100',
    surface: 'bg-white/90',
    surfaceHover: 'bg-white',
    headerBg: 'bg-slate-100',
    interactiveHover: 'hover:bg-slate-100',
    text: 'text-slate-900',
    textSecondary: 'text-slate-700',
    textMuted: 'text-slate-500',
    border: 'border-slate-200',
    borderLight: 'border-slate-100',
    divide: 'divide-slate-100',
    sidebarBackground: 'bg-gradient-to-b from-slate-900 via-slate-900 to-slate-800',
    sidebarText: 'text-slate-100',
    sidebarTextHover: 'text-white',
    sidebarItemHover: 'hover:bg-white/10 hover:text-white',
    sidebarActive: 'bg-teal-500/90',
    sidebarActiveText: 'text-white',
    sidebarBorder: 'border-slate-700',
    topbarBackground: 'bg-white/80 backdrop-blur-xl',
    topbarText: 'text-slate-900',
    topbarBorder: 'border-slate-200',
    primary: 'bg-teal-500',
    primaryHover: 'bg-teal-600',
    primaryText: 'text-white',
    primaryFg: 'text-teal-600',
    primaryBorder: 'border-l-teal-500',
    primaryBorderAll: 'border-teal-500',
    primaryChecked: 'peer-checked:bg-teal-500',
    primaryCheckedHover: 'peer-checked:hover:bg-teal-600',
    accent: 'bg-sky-500',
    accentHover: 'bg-sky-600',
    success: 'bg-emerald-600 text-white',
    warning: 'bg-amber-500 text-white',
    error: 'bg-rose-600 text-white',
    info: 'bg-sky-600 text-white',
  },
};

/** Scrollbar colors per theme (CSS custom property values) */
const scrollbarVars: Record<Theme, { track: string; thumb: string; thumbHover: string }> = {
  light: { track: '#f1f5f9', thumb: '#94a3b8', thumbHover: '#64748b' },
  dark: { track: '#0f172a', thumb: '#475569', thumbHover: '#64748b' },
  blue: { track: '#eff6ff', thumb: '#bfdbfe', thumbHover: '#93c5fd' },
  green: { track: '#f0fdf4', thumb: '#bbf7d0', thumbHover: '#86efac' },
  purple: { track: '#faf5ff', thumb: '#e9d5ff', thumbHover: '#d8b4fe' },
  macos: { track: '#e5e7eb', thumb: '#cbd5e1', thumbHover: '#9ca3af' },
};

const getDefaultSidebarCollapsedForTheme = (_theme: Theme): boolean => {
  return false;
};

interface ThemeContextType {
  theme: Theme;
  colors: ThemeColors;
  setTheme: (theme: Theme) => void;
  availableThemes: Theme[];
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = useState<Theme>(() => {
    // Load theme from localStorage or default to light
    const savedTheme = localStorage.getItem('app-theme') as Theme;
    return savedTheme && themes[savedTheme] ? savedTheme : 'light';
  });

  const [sidebarCollapsed, setSidebarCollapsedState] = useState<boolean>(() => {
    const saved = localStorage.getItem('app-sidebar-collapsed');
    if (saved === 'true' || saved === 'false') {
      return saved === 'true';
    }
    // If no saved value, derive from current theme
    return getDefaultSidebarCollapsedForTheme(
      (localStorage.getItem('app-theme') as Theme) || 'light'
    );
  });

  const setTheme = useCallback((newTheme: Theme) => {
    const collapsed = getDefaultSidebarCollapsedForTheme(newTheme);
    localStorage.setItem('app-theme', newTheme);
    localStorage.setItem('app-sidebar-collapsed', String(collapsed));
    // Defer state updates so the UI stays responsive (avoids freeze from mass re-renders)
    startTransition(() => {
      setThemeState(newTheme);
      setSidebarCollapsedState(collapsed);
    });
  }, []);

  const setSidebarCollapsed = useCallback((collapsed: boolean) => {
    localStorage.setItem('app-sidebar-collapsed', String(collapsed));
    setSidebarCollapsedState(collapsed);
  }, []);

  const colors = themes[theme];
  const availableThemes: Theme[] = ['light', 'dark', 'blue', 'green', 'purple', 'macos'];
  const scrollbar = scrollbarVars[theme];

  // Keep Tailwind `dark:` variants in sync with the app dark theme (class strategy).
  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }, [theme]);

  const value = useMemo(
    () => ({ theme, colors, setTheme, availableThemes, sidebarCollapsed, setSidebarCollapsed }),
    [theme, colors, setTheme, availableThemes, sidebarCollapsed, setSidebarCollapsed]
  );

  return (
    <ThemeContext.Provider value={value}>
      <div
        className={colors.background}
        data-theme={theme}
        style={
          {
            '--scrollbar-track': scrollbar.track,
            '--scrollbar-thumb': scrollbar.thumb,
            '--scrollbar-thumb-hover': scrollbar.thumbHover,
          } as React.CSSProperties
        }
      >
        {children}
      </div>
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

